import { useEffect, useRef } from 'react';
import { isModalOpen } from '../components/ui/Modal';

/**
 * Ctrl+F / Cmd+F in an extension sidebar focuses its filter (owner, 30.09.2026:
 * "if focus was in the left side bar of the DB, ctrl + f should focus the
 * Filter databases search; if focused on the table, trigger the table search").
 *
 * The DB grid's find is a window listener, so it answered Ctrl+F from
 * anywhere — including the sidebar — and the sidebar never got its own search.
 *
 * "In the sidebar" means focus is inside `containerRef`, OR the last click was
 * there: tree rows are not focusable, so clicking one leaves focus on <body>.
 * Runs in the capture phase and stops the event, so the grid's handler never
 * sees a Ctrl+F that belongs to the sidebar. A hidden sidebar (another panel
 * is active; the host keeps it mounted with display:none) never takes it.
 */
export default function useSidebarFilterShortcut(containerRef, inputRef) {
  const lastInside = useRef(false);

  useEffect(() => {
    const inside = (node) => !!(containerRef.current && node && containerRef.current.contains(node));
    const onPointer = (e) => {
      lastInside.current = inside(e.target);
    };
    const onFocus = (e) => {
      lastInside.current = inside(e.target);
    };
    const onKey = (e) => {
      if (!(e.ctrlKey || e.metaKey) || e.altKey || e.shiftKey || (e.key || '').toLowerCase() !== 'f') return;
      if (isModalOpen()) return; // a dialog opened from the sidebar keeps its own Ctrl+F
      const box = containerRef.current;
      // The host hides inactive panels with an inline display:none (ExtensionHostLayer).
      if (!box || box.closest('[style*="display: none"]')) return;
      if (!inside(document.activeElement) && !lastInside.current) return;
      e.preventDefault();
      e.stopPropagation();
      inputRef.current?.focus();
      inputRef.current?.select();
    };
    window.addEventListener('mousedown', onPointer, true);
    window.addEventListener('focusin', onFocus, true);
    window.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('mousedown', onPointer, true);
      window.removeEventListener('focusin', onFocus, true);
      window.removeEventListener('keydown', onKey, true);
    };
  }, [containerRef, inputRef]);
}
